/* Public Market page — CoinMBX light theme (matches landing page).
   Layout: 3 highlight rails (Top Gainers / Losers / Trending) + Markets table
   with category tabs + quote chips + 50/page pagination. Same structure as
   coinzii.com/market, rebuilt with our own tokens.
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Star, TrendingUp, TrendingDown, Flame, ChevronRight, ArrowRight,
  ChevronLeft, ChevronsRight, ChevronsLeft, Search, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import GetVerifiedModal from "@/components/GetVerifiedModal";
import CoinIcon from "@/components/CoinIcon";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const C = {
  navy: "#1D2D3F",
  surface: "#F5F7FB",
  border: "#E7E9F2",
  blue: "#022CED",
  grey: "#626262",
  gold: "#F0B83A",
  green: "#16A34A",
  red: "#DC2626",
  lavender: "#F1EEFF",
};

const FAV_LS_KEY = "coinmbx_market_favs_v1";

// --- Price flash animation ---------------------------------------------
// Compares latest price against the last-rendered price; briefly tints the
// cell background (green for up, red for down). Pure-CSS keyframes so it
// works even when the tab is mostly idle.
const FLASH_KEYFRAMES = `
@keyframes coinmbx-flash-up {
  0%   { background-color: rgba(22,163,74,0.22); }
  100% { background-color: rgba(22,163,74,0); }
}
@keyframes coinmbx-flash-down {
  0%   { background-color: rgba(220,38,38,0.22); }
  100% { background-color: rgba(220,38,38,0); }
}
.coinmbx-price-flash-up   { animation: coinmbx-flash-up 0.9s ease-out; }
.coinmbx-price-flash-down { animation: coinmbx-flash-down 0.9s ease-out; }
`;

function PriceCell({ value, fmt, quoteLabel, color }) {
  const prev = useRef(value);
  const [dir, setDir] = useState(null); // "up" | "down" | null

  useEffect(() => {
    const before = Number(prev.current);
    const after = Number(value);
    if (Number.isFinite(before) && Number.isFinite(after) && before !== after) {
      setDir(after > before ? "up" : "down");
      const t = setTimeout(() => setDir(null), 900);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
    return undefined;
  }, [value]);

  const cls = dir === "up"
    ? "coinmbx-price-flash-up"
    : dir === "down"
      ? "coinmbx-price-flash-down"
      : "";
  return (
    <td className={`px-4 py-4 text-right tabular-nums font-bold ${cls}`}
      style={{ color, transition: "background-color 0.2s" }}
      data-testid="mkt-price-cell"
      data-flash={dir || ""}>
      {fmt(value, quoteLabel)}
    </td>
  );
}

function fmtPrice(v, quote) {
  if (!Number.isFinite(v)) return "—";
  if (quote === "INR") return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 4 })}`;
  if (quote === "BTC") return `₿${v.toLocaleString("en-US", { maximumFractionDigits: 8 })}`;
  if (v < 0.01) return `$${v.toFixed(6)}`;
  if (v < 1) return `$${v.toFixed(4)}`;
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}
function fmtVol(v, quote) {
  if (!Number.isFinite(v) || v === 0) return "—";
  const sign = quote === "INR" ? "₹" : quote === "BTC" ? "₿" : "$";
  if (v >= 1e9) return `${sign}${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${sign}${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${sign}${(v / 1e3).toFixed(2)}K`;
  return `${sign}${v.toFixed(2)}`;
}
function fmtChange(v) {
  if (!Number.isFinite(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

function ChangeBadge({ value, size = 12 }) {
  if (!Number.isFinite(value)) return <span style={{ color: C.grey }}>—</span>;
  const positive = value >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const color = positive ? C.green : C.red;
  return (
    <span className="inline-flex items-center gap-0.5 tabular-nums font-bold" style={{ color }}>
      <Icon size={size} strokeWidth={2.5} />
      {`${positive ? "+" : ""}${value.toFixed(2)}%`}
    </span>
  );
}

// ============================================================================
export default function Market() {
  const [category, setCategory] = useState("all");
  const loc = useLocation();
  const { user } = useAuth();
  const [showVerify, setShowVerify] = useState(false);

  // Open the "Get Verified" modal after a fresh signup, OR when an unverified
  // user lands on /markets and hasn't dismissed it this session.
  useEffect(() => {
    if (loc.state?.justRegistered) {
      setShowVerify(true);
      // Wipe the navigation state so a refresh doesn't keep re-opening it.
      window.history.replaceState({}, document.title);
      return;
    }
    if (user && user.kyc_status !== "approved") {
      const dismissed = sessionStorage.getItem("coinmbx_get_verified_dismissed");
      if (!dismissed) setShowVerify(true);
    }
  }, [loc.state, user]);

  function dismissVerify() {
    sessionStorage.setItem("coinmbx_get_verified_dismissed", "1");
    setShowVerify(false);
  }

  function jumpToCategory(cat) {
    setCategory(cat);
    setTimeout(() => {
      document.getElementById("markets-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }
  return (
    <main data-testid="market-page" className="bg-white text-cm-navy font-sans min-h-screen">
      <Header />
      <Highlights onMore={jumpToCategory} />
      <MarketsSection category={category} setCategory={setCategory} />
      <Footer />
      <GetVerifiedModal open={showVerify} onClose={dismissVerify} />
    </main>
  );
}

// ----- Highlight rails -----
function Highlights({ onMore }) {
  const [data, setData] = useState({ gainers: [], losers: [], trending: [] });
  useEffect(() => {
    api.get("/api/markets/highlights").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  return (
    <section className="py-12" style={{ background: C.lavender }}>
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <RailCard title="Top Gainers" icon={TrendingUp} accent={C.green} items={data.gainers}
            testid="rail-gainers" onMore={() => onMore("gainers")} />
          <RailCard title="Top Losers"  icon={TrendingDown} accent={C.red}   items={data.losers}
            testid="rail-losers" onMore={() => onMore("losers")} />
          <RailCard title="Trending"    icon={Flame}        accent={C.gold}  items={data.trending}
            testid="rail-trending" onMore={() => onMore("all")} />
        </div>
      </div>
    </section>
  );
}

function RailCard({ title, icon: Icon, accent, items, testid, onMore }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLoaded(true), 4000); return () => clearTimeout(t); }, []);
  return (
    <div data-testid={testid}
      className="bg-white rounded-[16px] border p-5 shadow-[0_8px_24px_-16px_rgba(2,44,237,0.12)]"
      style={{ borderColor: C.border }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: accent }} />
          <h3 className="font-bold text-[16px]" style={{ color: C.navy }}>{title}</h3>
        </div>
        <button type="button" onClick={onMore}
          data-testid={`${testid}-more`}
          className="text-[12px] flex items-center gap-1 font-semibold transition hover:opacity-80"
          style={{ color: C.blue }}>
          More <ChevronRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-3 text-[10px] uppercase tracking-wider pb-2 border-b" style={{ borderColor: C.border, color: C.grey }}>
        <span>Pairs</span><span className="text-right">Price</span><span className="text-right">24h Change</span>
      </div>
      <div className="divide-y" style={{ borderColor: C.border }}>
        {items.length === 0 && (
          <div className="text-center py-8 text-[12px]" style={{ color: C.grey }}>
            {loaded ? "Temporarily unavailable — refreshing" : "Loading…"}
          </div>
        )}
        {items.map((it) => (
          <Link key={`${title}-${it.symbol}`} to={`/trade/spot/${it.symbol}-USDT`}
            data-testid={`${testid}-item-${it.symbol}`}
            className="grid grid-cols-3 items-center py-3 transition rounded-md px-1 -mx-1 hover:bg-[#F5F7FB]">
            <div className="flex items-center gap-2 min-w-0">
              <CoinIcon symbol={it.symbol} image={it.image} size={28} />
              <span className="font-bold text-[13px] truncate" style={{ color: C.navy }}>{it.symbol}/USDT</span>
            </div>
            <span className="text-right tabular-nums text-[13px] font-bold" style={{ color: C.navy }}>{fmtPrice(it.price, "USDT")}</span>
            <span className="text-right text-[12px]">
              <ChangeBadge value={it.change_pct_24h} size={11} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ----- Markets section -----
const CATEGORIES = [
  { id: "favorites", label: "Favorites" },
  { id: "all",       label: "All" },
  { id: "spot",      label: "Spot" },
  { id: "new",       label: "New Listings" },
  { id: "gainers",   label: "Gainers" },
  { id: "losers",    label: "Losers" },
];
const QUOTES = ["All", "USDT", "USDC", "BTC", "INR"];

function MarketsSection({ category, setCategory }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [quote, setQuote] = useState("USDT");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState({ rows: [], total: 0, total_pages: 1, page: 1, page_size: 100 });
  const [loading, setLoading] = useState(false);
  const [favs, setFavs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FAV_LS_KEY) || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => {
    if (user && user.id) {
      api.get("/api/markets/favorites").then(({ data }) => setFavs(new Set(data.symbols))).catch(() => {});
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); }, [category, quote, debouncedSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Inject flash keyframes once per page mount
  useEffect(() => {
    if (document.getElementById("coinmbx-flash-keyframes")) return undefined;
    const style = document.createElement("style");
    style.id = "coinmbx-flash-keyframes";
    style.textContent = FLASH_KEYFRAMES;
    document.head.appendChild(style);
    return undefined;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { quote, category, page, page_size: 100 };
    if (debouncedSearch) params.search = debouncedSearch;
    if (category === "favorites" && user?.id) params.user_id = user.id;
    const fetchOnce = (silent) => api.get("/api/markets/list", { params })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { /* keep prior data */ })
      .finally(() => { if (!cancelled && !silent) setLoading(false); });
    fetchOnce(false);
    // Silent 5s poll so price cells flash live without remounting the table
    const t = setInterval(() => fetchOnce(true), 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [quote, category, page, debouncedSearch, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRows = useMemo(() => {
    let rows = data.rows;
    if (category === "favorites" && (!user || !user.id)) {
      rows = rows.filter((r) => favs.has(r.symbol));
    }
    return rows;
  }, [data.rows, category, favs, user]);

  async function toggleFav(symbol) {
    if (user && user.id) {
      try {
        const { data } = await api.post(`/api/markets/favorites/${symbol}/toggle`);
        setFavs(new Set(data.symbols));
        return;
      } catch { /* fallthrough */ }
    }
    const next = new Set(favs);
    if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
    setFavs(next);
    localStorage.setItem(FAV_LS_KEY, JSON.stringify([...next]));
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1320px] mx-auto px-6">
        <h2 className="text-[28px] sm:text-[36px] font-bold mb-8" style={{ color: C.navy }}>Markets</h2>

        {/* Filter strip */}
        <div className="bg-white rounded-[16px] border p-5 mb-5" style={{ borderColor: C.border }}>
          <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
            {CATEGORIES.map((c) => {
              const active = category === c.id;
              return (
                <button key={c.id} onClick={() => setCategory(c.id)}
                  data-testid={`mkt-cat-${c.id}`}
                  className="h-[36px] px-4 rounded-full text-[13px] font-bold whitespace-nowrap transition"
                  style={active
                    ? { background: C.gold, color: C.navy }
                    : { background: C.surface, color: C.grey, border: `1px solid ${C.border}` }}>
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {QUOTES.map((q) => {
              const active = quote === q;
              return (
                <button key={q} onClick={() => setQuote(q)}
                  data-testid={`mkt-quote-${q}`}
                  className="h-[32px] px-3.5 rounded-md text-[12px] font-bold whitespace-nowrap transition"
                  style={active
                    ? { background: `${C.gold}22`, color: "#A57A1F", border: `1px solid ${C.gold}` }
                    : { background: "#fff", color: C.grey, border: `1px solid ${C.border}` }}>
                  {q}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2 px-3 h-[36px] rounded-md border min-w-[220px]"
              style={{ borderColor: C.border, background: "#fff" }}>
              <Search size={13} style={{ color: C.grey }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search pair…" data-testid="mkt-search"
                className="bg-transparent outline-none text-[13px] flex-1" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-[16px] border overflow-hidden bg-white" style={{ borderColor: C.border }}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="mkt-table">
              <thead style={{ background: C.surface }}>
                <tr className="text-[11px] uppercase tracking-wider" style={{ color: C.grey }}>
                  <th className="px-4 py-3 w-[40px]"></th>
                  <th className="px-4 py-3 text-left font-semibold">Pair</th>
                  <th className="px-4 py-3 text-right font-semibold">Price</th>
                  <th className="px-4 py-3 text-right font-semibold">24h Change</th>
                  <th className="px-4 py-3 text-right font-semibold">24h High</th>
                  <th className="px-4 py-3 text-right font-semibold">24h Low</th>
                  <th className="px-4 py-3 text-right font-semibold">24h Volume</th>
                  <th className="px-4 py-3 text-center font-semibold w-[100px]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: C.border }}>
                {loading && visibleRows.length === 0 && (
                  <tr><td colSpan={8} className="p-12 text-center" style={{ color: C.grey }}>Loading markets…</td></tr>
                )}
                {!loading && visibleRows.length === 0 && (
                  <tr><td colSpan={8} className="p-16 text-center text-[14px]" style={{ color: C.grey }}>
                    {category === "favorites"
                      ? "No favorites yet. Tap the star next to any pair to add it here."
                      : "No pairs match your filters."}
                  </td></tr>
                )}
                {visibleRows.map((r) => {
                  const fav = favs.has(r.symbol);
                  const quoteLabel = r.pair.split("/")[1] || quote;
                  return (
                    <tr key={`${r.symbol}-${quoteLabel}`} className="hover:bg-[#FAFBFE] transition cursor-pointer"
                      data-testid={`mkt-row-${r.symbol}`}
                      onClick={() => nav(`/trade/spot/${r.symbol}-${quoteLabel}`)}>
                      <td className="px-4 py-4">
                        <button onClick={(e) => { e.stopPropagation(); toggleFav(r.symbol); }}
                          data-testid={`mkt-fav-${r.symbol}`}
                          aria-label="Toggle favorite"
                          className="w-7 h-7 grid place-items-center rounded-md transition hover:bg-[#F1EEFF]">
                          <Star size={14}
                            fill={fav ? C.gold : "transparent"}
                            style={{ color: fav ? C.gold : "#C2C7D6" }} />
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <CoinIcon symbol={r.symbol} image={r.image} size={32} />
                          <div>
                            <div className="font-bold text-[14px]" style={{ color: C.navy }}>{r.symbol}/{quoteLabel}</div>
                            <div className="text-[11px]" style={{ color: C.grey }}>{r.name}</div>
                          </div>
                        </div>
                      </td>
                      <PriceCell value={r.price} fmt={fmtPrice} quoteLabel={quoteLabel} color={C.navy} />
                      <td className="px-4 py-4 text-right">
                        <span className="inline-flex justify-end w-full">
                          <ChangeBadge value={r.change_pct_24h} size={13} />
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums" style={{ color: C.grey }}>{fmtPrice(r.high_24h, quoteLabel)}</td>
                      <td className="px-4 py-4 text-right tabular-nums" style={{ color: C.grey }}>{fmtPrice(r.low_24h, quoteLabel)}</td>
                      <td className="px-4 py-4 text-right tabular-nums" style={{ color: C.grey }}>{fmtVol(r.volume_24h, quoteLabel)}</td>
                      <td className="px-4 py-4 text-center">
                        <Link to={`/trade/spot/${r.symbol}-${quoteLabel}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`mkt-trade-${r.symbol}`}
                          className="inline-flex items-center gap-1 h-[32px] px-4 rounded-full font-bold text-[12px] transition hover:opacity-90"
                          style={{ background: C.gold, color: C.navy }}>
                          Trade <ArrowRight size={11} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.total > 0 && (
            <div className="flex flex-wrap items-center justify-between px-5 py-4 border-t gap-3" style={{ borderColor: C.border, background: C.surface }}>
              <span className="text-[12px]" style={{ color: C.grey }}>
                Showing {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} of {data.total} results
              </span>
              <div className="flex items-center gap-1.5">
                <PgBtn onClick={() => setPage(1)} disabled={page === 1}><ChevronsLeft size={13} /></PgBtn>
                <PgBtn onClick={() => setPage(page - 1)} disabled={page === 1}><ChevronLeft size={13} /></PgBtn>
                {[...Array(data.total_pages || 1)].map((_, i) => {
                  const p = i + 1;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      data-testid={`mkt-page-${p}`}
                      className="w-9 h-9 rounded-md text-[13px] font-bold transition"
                      style={p === page
                        ? { background: C.gold, color: C.navy }
                        : { background: "#fff", color: C.grey, border: `1px solid ${C.border}` }}>
                      {p}
                    </button>
                  );
                })}
                <PgBtn onClick={() => setPage(page + 1)} disabled={page === data.total_pages}><ChevronRight size={13} /></PgBtn>
                <PgBtn onClick={() => setPage(data.total_pages)} disabled={page === data.total_pages}><ChevronsRight size={13} /></PgBtn>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function PgBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="w-9 h-9 grid place-items-center rounded-md transition disabled:opacity-40 hover:bg-white"
      style={{ background: "#fff", color: C.grey, border: `1px solid ${C.border}` }}>
      {children}
    </button>
  );
}
